import { isMobileDevice, getGSAP } from './utils.js';

/**
 * Lenis smooth scrolling controller
 * Manages smooth scrolling behavior and integrates with GSAP ScrollTrigger
 * モバイルではLenisを初期化せず、ネイティブスクロールを使用
 */
export default class LenisController {
  constructor(options = {}) {
    this.lenis = null;
    this.gsap = null;
    this.ScrollTrigger = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.anchorClickHandlers = new Map();
    this.isMobile = isMobileDevice();
    this.isInitialized = false;

    // Don't initialize Lenis if user prefers reduced motion
    if (this.prefersReducedMotion) {
      return;
    }

    // モバイルではLenisを初期化しない（ネイティブスクロールを使用）
    // iOS Safariで慣性スクロールが阻害される問題を回避
    if (this.isMobile) {
      return;
    }

    this.options = {
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      infinite: false,
      wheelMultiplier: 1.0,
      normalizeWheel: true,
      // Prevent Lenis from handling scroll on specific elements
      prevent: (node) => {
        try {
          if (!node || typeof node.closest !== 'function') return false;
          return Boolean(node.closest('[data-lenis-prevent]'));
        } catch (error) {
          console.warn('[Lenis] Error in prevent callback:', error);
          return false;
        }
      },
      ...options
    };

    this.init();
  }

  async init() {
    try {
      // Lenisと共通GSAP初期化を並列で実行
      const [{ default: Lenis }, { gsap, ScrollTrigger }] = await Promise.all([
        import('lenis'),
        getGSAP()
      ]);

      this.gsap = gsap;
      this.ScrollTrigger = ScrollTrigger;

      this.lenis = new Lenis(this.options);

      // GSAP ScrollTriggerとLenisの連携
      this.lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => {
        this.lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);

      // Setup scroll event delegation
      this.setupScrollEvents();

      // Setup anchor link handling
      this.setupAnchorLinks();

      this.isInitialized = true;
    } catch (error) {
      console.error('[Lenis] Failed to initialize:', error);
    }
  }

  setupScrollEvents() {
    if (!this.lenis) return;

    // Forward lenis scroll events to window for compatibility
    this.lenis.on('scroll', (e) => {
      window.dispatchEvent(new CustomEvent('lenis-scroll', {
        detail: {
          scroll: e.scroll,
          velocity: e.velocity,
          direction: e.direction,
          progress: e.progress
        }
      }));
    });
  }

  /**
   * Handle anchor link clicks with smooth scrolling
   */
  setupAnchorLinks() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      const handler = (e) => {
        const href = anchor.getAttribute('href');
        if (href === '#') return;

        const target = document.getElementById(href.replace('#', ''));
        if (target) {
          e.preventDefault();
          this.scrollTo(target);
        }
      };

      // Store handler for cleanup
      this.anchorClickHandlers.set(anchor, handler);
      anchor.addEventListener('click', handler);
    });
  }

  /**
   * Remove anchor link event listeners
   */
  removeAnchorLinks() {
    this.anchorClickHandlers.forEach((handler, anchor) => {
      anchor.removeEventListener('click', handler);
    });
    this.anchorClickHandlers.clear();
  }

  // Public API methods
  scrollTo(target, options = {}) {
    if (!this.lenis) {
      // Fallback for reduced motion or if Lenis failed to initialize
      const element = typeof target === 'string'
        ? document.querySelector(target)
        : target;
      if (element) {
        element.scrollIntoView({ behavior: 'auto' });
      }
      return;
    }
    this.lenis.scrollTo(target, options);
  }

  stop() {
    if (!this.lenis) return;
    this.lenis.stop();
  }

  start() {
    if (!this.lenis) return;
    this.lenis.start();
  }

  destroy() {
    // Remove anchor link handlers
    this.removeAnchorLinks();

    // Destroy Lenis instance
    if (this.lenis) {
      this.lenis.destroy();
      this.lenis = null;
    }
  }

  // Instance getter for external access
  getInstance() {
    return this.lenis;
  }
}
